import { array, boolean, mixed, object, string } from 'yup';

import { createBulkLeadReminderInitialValues } from '@src/features/participants/components/modals/send-bulk-reminder-to-student-modal/send-bulk-reminder.schema';

import type { TProgram } from '@src/shared/types/program';
import {
  REMINDER_CHANNELS,
  REMINDER_TRIGGER_TYPES,
  REMINDER_TYPES,
  type TReminder,
} from '@src/shared/types/reminder';
import { RULE_TYPES } from '@src/shared/types/rule';

const RULE_TYPES_WITH_CHOICES: string[] = [
  RULE_TYPES.trueFalse,
  RULE_TYPES.applicationSubmission,
];
import { dayjsUtils } from '@src/shared/utils/helpers/dayjs';
import {
  getCurrentScheduleFields,
  getUserTimezone,
} from '../../utils/get-current-schedule-fields';

export type TAudienceGroup = 'program' | 'leads';

export type TCreateReminderFormValues = ReturnType<
  typeof createReminderInitialValues
> & {
  selectedLeadIds?: number[];
  selectedMemberIds?: number[];
  type?: TReminder['type'] | '';
  trigger_type?: TReminder['trigger_type'] | '';
  date?: string;
  frequency?: string | number;
  frequency_count?: number;
  rule?: string;
  stages?: number[];
  question?: string;
  is_rule_achieved?: string;
  rule_choices?: string[];
  rule_type?: string;
  repeatSending?: boolean;
  flight_type?: TReminder['flight_type'] | '';
  submission_date_type?: TReminder['submission_date_type'] | '';
};

export function createReminderInitialValues() {
  const base = createBulkLeadReminderInitialValues();
  const { date: _droppedDate, ...rest } = base;
  void _droppedDate;

  const sendNowFields = getCurrentScheduleFields();

  return {
    ...rest,
    audienceGroup: 'program' as TAudienceGroup,
    program: null as TProgram | null,
    scheduleDelivery: false,
    ...sendNowFields,
  };
}

export function createLeadsReminderInitialValues() {
  return {
    ...createReminderInitialValues(),
    audienceGroup: 'leads' as TAudienceGroup,
    program: null,
    selectedLeadIds: [] as number[],
  };
}

export function createParticipantsReminderInitialValues() {
  return {
    ...createReminderInitialValues(),
    view: undefined,
    audienceGroup: 'program' as TAudienceGroup,
    program: null,
    selectedMemberIds: [] as number[],
  };
}

export function createBulkStageReminderInitialValues(
  defaultStageIds: number[] = []
) {
  return {
    ...createReminderInitialValues(),
    view: undefined,
    audienceGroup: 'program' as TAudienceGroup,
    program: null,
    stages: defaultStageIds,
    selectedMemberIds: [] as number[],
  };
}

export function createBulkStageReminderValidationSchema() {
  return createReminderSchema.shape({
    view: mixed().nullable(),
    program: mixed().nullable(),
    audienceGroup: string().nullable(),
    stages: array().when('selectedMemberIds', {
      is: (ids: number[] | undefined) => !ids?.length,
      then: (schema) =>
        schema
          .min(1, 'Please select at least one stage.')
          .required('Please select at least one stage.'),
      otherwise: (schema) => schema.nullable(),
    }),
    selectedMemberIds: array().of(mixed()).default([]),
  });
}

export function createStageReminderInitialValues() {
  return {
    ...createReminderInitialValues(),
    view: undefined,
    audienceGroup: 'program' as TAudienceGroup,
    program: null,
    type: '' as TReminder['type'] | '',
    trigger_type: REMINDER_TRIGGER_TYPES.before,
    date: '',
    frequency: 0,
    frequency_count: 0,
    rule: '',
    stages: [] as number[],
    question: '',
    is_rule_achieved: '',
    rule_choices: [] as string[],
    rule_type: '',
    scheduleDelivery: false,
    repeatSending: false,
    flight_type: '' as TReminder['flight_type'] | '',
  };
}

export function createStageReminderValidationSchema() {
  return createReminderSchema.shape({
    view: mixed().nullable(),
    program: mixed().nullable(),
    audienceGroup: string().oneOf(['program', 'leads']).nullable(),
    type: string().required('Type is required'),
    trigger_type: string().nullable(),
    repeatSending: boolean().when('type', {
      is: REMINDER_TYPES.otherRules,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    frequency: string().when(['type', 'trigger_type', 'repeatSending'], {
      is: (type: string, triggerType: string, repeatSending: boolean) => {
        const isProgramDateType = type === REMINDER_TYPES.programDateRelated;
        const isOtherRuleType =
          type === REMINDER_TYPES.otherRules && repeatSending;
        const isBeforeAfterTrigger = triggerType !== REMINDER_TRIGGER_TYPES.on;

        return isOtherRuleType || (isProgramDateType && isBeforeAfterTrigger);
      },
      then: (schema) => schema.required('Please enter days'),
      otherwise: (schema) => schema.nullable(),
    }),
    frequency_count: string().when(['type', 'frequency', 'repeatSending'], {
      is: (type: string, frequency: string, repeatSending: boolean) =>
        type === REMINDER_TYPES.otherRules && repeatSending && frequency,
      then: (schema) => schema.required('Please enter frequency count'),
      otherwise: (schema) => schema.nullable(),
    }),
    rule: string().nullable(),
    stages: array().of(mixed()).nullable(),
    question: string().nullable(),
    is_rule_achieved: string().nullable(),
    rule_type: string().nullable(),
    rule_choices: array()
      .of(string())
      .nullable()
      .when(['type', 'rule_type'], {
        is: (type: string, ruleType: string) =>
          type === REMINDER_TYPES.otherRules &&
          RULE_TYPES_WITH_CHOICES.includes(ruleType),
        then: (schema) => schema.min(1, 'Please select at least one choice'),
      }),
    scheduleDelivery: boolean().when('type', {
      is: REMINDER_TYPES.specificDate,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
    scheduledDate: string().when('type', {
      is: REMINDER_TYPES.specificDate,
      then: (schema) =>
        schema
          .required('Date is required')
          .test('not-in-past', 'Date cannot be in the past', function (value) {
            if (!value) return true;
            const tz =
              (this.parent as { timezone?: string }).timezone ||
              getUserTimezone();
            const today = dayjsUtils().tz(tz).format('YYYY-MM-DD');
            return value >= today;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    scheduledTime: string().when('type', {
      is: REMINDER_TYPES.specificDate,
      then: (schema) => schema.required('Time is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    timezone: string().when('type', {
      is: REMINDER_TYPES.specificDate,
      then: (schema) => schema.required('Time zone is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    flight_type: string().when('type', {
      is: REMINDER_TYPES.flightInfo,
      then: (schema) => schema.required('Flight type is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
}

export function createLeadsReminderValidationSchema() {
  return createReminderSchema.shape({
    view: mixed().when('selectedLeadIds', {
      is: (ids: number[] | undefined) => !ids?.length,
      then: (schema) => schema.required('Please select a saved view.'),
      otherwise: (schema) => schema.nullable(),
    }),
    program: mixed().nullable(),
    selectedLeadIds: array().of(mixed()).default([]),
  });
}

export function createParticipantsReminderValidationSchema() {
  return createReminderSchema.shape({
    view: mixed().when('selectedMemberIds', {
      is: (ids: number[] | undefined) => !ids?.length,
      then: (schema) => schema.required('Please select a saved view.'),
      otherwise: (schema) => schema.nullable(),
    }),
    program: mixed().nullable(),
    audienceGroup: string().nullable(),
    selectedMemberIds: array().of(mixed()).default([]),
  });
}

export const createReminderSchema = object().shape({
  attachments: array().of(mixed()),
  name: string().required('Name is required'),
  content: string().required('Content is required'),
  view: mixed().required('Please select a saved view.'),
  notification_channel: string().required('Communication channel is required'),
  subject: string().when('notification_channel', {
    is: REMINDER_CHANNELS.email,
    then: (schema) => schema.required('Subject is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  variables: object(),
  template: mixed().nullable().required('Please select a template.'),
  audienceGroup: string().oneOf(['program', 'leads']).required(),
  program: mixed().when('audienceGroup', {
    is: 'program',
    then: (schema) => schema.required('Please select a program.'),
    otherwise: (schema) => schema.nullable(),
  }),
  scheduleDelivery: boolean().required(),
  scheduledDate: string().when('scheduleDelivery', {
    is: true,
    then: (schema) =>
      schema
        .required('Date is required')
        .test('not-in-past', 'Date cannot be in the past', function (value) {
          if (!value) return true;
          const tz =
            (this.parent as { timezone?: string }).timezone ||
            getUserTimezone();
          const today = dayjsUtils().tz(tz).format('YYYY-MM-DD');
          return value >= today;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  scheduledTime: string().when('scheduleDelivery', {
    is: true,
    then: (schema) => schema.required('Time is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  timezone: string().when('scheduleDelivery', {
    is: true,
    then: (schema) => schema.required('Time zone is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  flight_type: string().when('type', {
    is: REMINDER_TYPES.flightInfo,
    then: (schema) => schema.required('Flight type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});
